import { generatePath } from 'react-router-dom';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Filters, Response } from '@ui/src';
import { Card, CardStatus, Customer } from '../../types';
import { cardsApi } from '../services';
import { CardsRoutes } from '../../../../routes';

export interface CardsListState {
  isLoading: boolean;
  requestInProgress: boolean;
  values?: Card[];
  sort?: {
    sortProp: string;
    sortDir: string;
  };
  filters?: Filters;
  selectedList: string[];
  status?: CardStatus;
  changeStatusInProgress?: boolean;
  customers?: Customer[];
}

const initialState: CardsListState = {
  isLoading: false,
  values: undefined,
  sort: undefined,
  requestInProgress: false,
  filters: undefined,
  selectedList: [],
  status: undefined,
};

export const cardsListSlice = createSlice({
  name: 'cardsList',
  initialState,
  reducers: {
    getCards: (
      state,
      action: PayloadAction<{
        name?: string;
        direction?: 'asc' | 'desc';
        filters?: Filters;
      }>,
    ) => {
      state.isLoading = true;

      if (action?.payload) {
        if (action.payload.name && action.payload.direction) {
          state.sort = {
            sortDir: action.payload.direction,
            sortProp: action.payload.name,
          };
        }

        if (action.payload.filters) {
          state.filters = action.payload.filters;
        }
      }
    },
    getCardsSuccess: (state, action: PayloadAction<Card[]>) => {
      state.isLoading = false;
      state.values = action.payload.map((item) => ({
        ...item,
        url: generatePath(CardsRoutes.CARD_INFO, { cardId: item.id }),
      }));
      state.requestInProgress = false;
    },
    onSortList: (
      state,
      action: PayloadAction<{ name: string; direction: 'asc' | 'desc' }>,
    ) => {
      state.sort = {
        sortDir: action.payload.direction,
        sortProp: action.payload.name,
      };
      state.requestInProgress = true;
    },
    onSortListSuccess: (state, action: PayloadAction<Card[]>) => {
      state.requestInProgress = false;
      state.values = action.payload;
    },
    selectList: (state, action: PayloadAction<string[]>) => {
      state.selectedList = action.payload;
    },
    onFilterList: (state, action: PayloadAction<Filters>) => {
      state.requestInProgress = true;
      state.filters = action.payload;
    },
    onFilterListSuccess: (state, action: PayloadAction<Card[]>) => {
      state.requestInProgress = false;
      state.values = action.payload;
    },
    changeStatus: (state, action: PayloadAction<CardStatus>) => {
      state.changeStatusInProgress = true;
      state.status = action.payload;
    },
    changeStatusSuccess: (state, action: PayloadAction<Card[]>) => {
      state.changeStatusInProgress = false;
      state.status = undefined;
      state.values = action.payload;
    },
    clearCardsListState: (state) => {
      state.isLoading = true;
      state.requestInProgress = false;
      state.values = undefined;
      state.selectedList = [];
      state.changeStatusInProgress = undefined;
      state.status = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      cardsApi.endpoints.getCustomers.matchFulfilled,
      (state, action: PayloadAction<Response<Customer>>) => {
        state.customers = action.payload.data.edges.map((item) => ({
          id: item.node.id,
          firstName: item.node.firstName,
          lastName: item.node.lastName,
        }));
      },
    );
  },
});

export const {
  getCards,
  getCardsSuccess,
  onSortList,
  onSortListSuccess,
  onFilterList,
  selectList,
  onFilterListSuccess,
  changeStatus,
  changeStatusSuccess,
} = cardsListSlice.actions;

export default cardsListSlice.reducer;
